// src/pages/CheckoutPage.jsx
import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../CartContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { AppContext } from '../AppContext';

const STORAGE_KEY = 'sf_cart_v1';
const CHECKOUT_PREF_KEY = 'sf_checkout_wallet_pref'; // 'wallet' | 'stripe'
const CURRENCY = 'USD';

// Pricing constants (mirrors backend)
const FLAT_SHIP_CENTS = 999;       // $9.99 flat shipping
const NASHVILLE_TAX_RATE = 0.0925; // 9.25% (TN 7% + Davidson 2.25%), simplified
const STRIPE_PCT = 0.029;
const STRIPE_FIXED_CENTS = 30;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { lines = [], updateCart } = useCart();
  const { isLoggedIn, currentUser, tokensUtility } = useContext(AppContext);

  // Determine signed-in via currentUser (empty if not logged in)
  const signedIn = !!(currentUser && Object.keys(currentUser).length);

    useEffect(() => {
    if (!currentUser) navigate('/login', { state: { from: '/checkout' } });
  }, [currentUser, navigate]);

  // Hydrate cart from localStorage if needed
  useEffect(() => {
    if (lines.length === 0) {
      try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        const arr = saved?.lines || [];
        if (arr.length) {
          const qty = arr.reduce((s,l)=>s+Number(l.qty||0),0);
          updateCart?.({ qty, lines: arr });
        }
      } catch {}
    }
  }, [lines.length, updateCart]);

  // Minimal contact fields (shipping collected by Stripe)
  const [contact, setContact] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const onContact = (k, v) => setContact(prev => ({ ...prev, [k]: v }));

  // Wallet toggle
  const [useWallet, setUseWallet] = useState(() => {
    try { return localStorage.getItem(CHECKOUT_PREF_KEY) === 'wallet'; } catch { return false; }
  });
  // If user signs out, force wallet off
  useEffect(() => {
    if (!signedIn && useWallet) setUseWallet(false);
  }, [signedIn, useWallet]);
  useEffect(() => {
    try { localStorage.setItem(CHECKOUT_PREF_KEY, useWallet ? 'wallet' : 'stripe'); } catch {}
  }, [useWallet]);

  // Subtotal from cart (display only; backend re-checks)
  const subtotalCents = useMemo(
    () => lines.reduce((s, l) => s + Number(l.priceCents || 0) * Number(l.qty || 0), 0),
    [lines]
  );

  // Utility token balance (1 token == $1) → cents
  const tokensUtilityCents = useMemo(
    () => Math.max(0, Math.round(Number(tokensUtility || 0) * 100)),
    [tokensUtility]
  );

  // Local Nashville-only tax (applies to subtotal + shipping)
  const shippingCents = FLAT_SHIP_CENTS;
  const taxBaseCents  = subtotalCents + shippingCents;
  const taxCents      = Math.round(taxBaseCents * NASHVILLE_TAX_RATE);

  // Totals
  const totalBeforeWalletCents = subtotalCents + shippingCents + taxCents;
  const walletAppliedCents = useMemo(
    () => (signedIn && useWallet ? Math.min(tokensUtilityCents, totalBeforeWalletCents) : 0),
    [signedIn, useWallet, tokensUtilityCents, totalBeforeWalletCents]
  );
  const remainderNetCents = Math.max(0, totalBeforeWalletCents - walletAppliedCents);

  // Stripe fee on remainder only (gross-up so you net remainderNetCents)
  const stripeGrossCents = useMemo(() => {
    if (remainderNetCents <= 0) return 0;
    return Math.ceil((remainderNetCents + STRIPE_FIXED_CENTS) / (1 - STRIPE_PCT));
  }, [remainderNetCents]);

  const stripeFeeCents = Math.max(0, stripeGrossCents - remainderNetCents);
  const totalDueCents  = stripeGrossCents; // what user pays at Stripe, if any

  const fmtUSD = (c) => (c/100).toLocaleString(undefined, { style: 'currency', currency: CURRENCY });

  const [loading, setLoading] = useState(false);

  const validate = () => {
    if (!lines.length) return 'Your cart is empty.';
    const nameOk = String(contact.fullName || '').trim().length > 0;
    const emailStr = String(contact.email || '').trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
    if (!nameOk) return 'Please enter your full name.';
    if (!emailOk) return 'Please enter a valid email address.';
    return '';
  };

  const startPayment = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);

    try {
      const functions = getFunctions();
      const effectiveUseWallet = signedIn && useWallet;

      if (totalDueCents === 0) {
        // Wallet covers everything (subtotal + shipping + tax)
        const place = httpsCallable(functions, 'completeWalletOnlyOrderStreetFlavor'); // implement server-side
        const payload = {
          items: lines.map(l => ({ id: l.lineId, qty: l.qty })), // server re-checks prices
          contact: {
            name: contact.fullName || '',
            email: contact.email || '',
            phone: contact.phone || '',
          },
          quote: {
            subtotalCents,
            shippingCents,
            taxCents,
            walletAppliedCents,
            totalCents: totalBeforeWalletCents,
          }
        };
        const { data } = await place(payload);
        if (data?.ok) {
          toast.success('Order placed with wallet!');
          updateCart?.({ qty: 0, lines: [] });
          try { localStorage.removeItem(STORAGE_KEY); } catch {}
          navigate(data?.redirectUrl || '/order-confirmation');
          return;
        }
        toast.error(data?.error?.message || 'Could not place order.');
        return;
      }

      // Otherwise, create Stripe payment link for the remainder; backend recomputes Nashville tax
      const linkFn = httpsCallable(functions, 'createPaymentLinkStreetFlavorMerch');
      const payload = {
        items: lines.map(l => ({ id: l.lineId, qty: l.qty })), // server re-checks
        useWallet: effectiveUseWallet, // tell backend intent; backend uses its trusted balance/price
        contact: {
          name: contact.fullName || '',
          email: contact.email || '',
          phone: contact.phone || '',
        },
        redirectUrl: `${window.location.origin}/order-confirmation`,
      };
      const { data } = await linkFn(payload);
      if (data?.ok && data?.url) {
        window.location.href = data.url;
      } else {
        toast.error(data?.error?.message || 'Could not start checkout.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Checkout failed to start.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sf-checkout">
      <h1 className="sf-title">Checkout</h1>

      <div className="sf-grid">
        <form className="sf-panel" onSubmit={(e)=>{e.preventDefault(); startPayment();}}>
          {/* Wallet toggle (only for signed-in users) ABOVE Contact header */}
          {signedIn && (
            <div className="sf-wallet-toggle">
              <label className="sf-switch">
                <input
                  type="checkbox"
                  checked={useWallet}
                  onChange={(e)=>setUseWallet(e.target.checked)}
                  disabled={loading}
                />
                <span className="sf-slider" />
              </label>
              <div className="sf-wallet-copy">
                <div className="sf-wallet-title">Use my wallet</div>
                <div className="sf-wallet-sub">
                  Balance: <strong>{fmtUSD(tokensUtilityCents)}</strong>
                </div>
              </div>
            </div>
          )}

          <h2 className="sf-h2">Contact</h2>

          {/* Required contact fields */}
          <label className="sf-label">
            Full Name (required)
            <input
              className="sf-input"
              value={contact.fullName}
              onChange={e=>onContact('fullName', e.target.value)}
              placeholder="Jane Doe"
              disabled={loading}
              required
            />
          </label>

          <div className="sf-row">
            <label className="sf-label half">
              Email (required)
              <input
                className="sf-input"
                type="email"
                value={contact.email}
                onChange={e=>onContact('email', e.target.value)}
                placeholder="jane@example.com"
                disabled={loading}
                required
              />
            </label>
            <label className="sf-label half">
              Phone (optional)
              <input
                className="sf-input"
                value={contact.phone}
                onChange={e=>onContact('phone', e.target.value)}
                placeholder="(555) 555-5555"
                disabled={loading}
              />
            </label>
          </div>

          <button type="submit" className="sf-btn" disabled={lines.length === 0 || loading} aria-busy={loading}>
            {loading
              ? (totalDueCents === 0 ? 'Placing Order…' : 'Opening Checkout…')
              : (totalDueCents === 0 ? 'Place Order (Wallet)' : 'Continue to Payment')}
          </button>
          <button type="button" className="sf-btn ghost" onClick={()=>!loading && navigate('/shop')} disabled={loading}>
            Back to Shop
          </button>
        </form>

        <aside className="sf-panel">
          <h2 className="sf-h2">Order Summary</h2>

          {!lines.length ? (
            <p className="sf-empty">Your cart is empty.</p>
          ) : (
            <>
              <ul className="sf-items">
                {lines.map(l => (
                  <li key={l.lineId} className="sf-item">
                    <img src={l.image} alt={l.title} />
                    <div className="sf-item-info">
                      <div className="sf-item-title">{l.title}</div>
                      <div className="sf-item-meta">Qty {l.qty}</div>
                    </div>
                    <div className="sf-item-price">
                      {(l.priceCents/100).toLocaleString(undefined, { style:'currency', currency:CURRENCY })}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="sf-totals">
                <div><span>Subtotal</span><span>{fmtUSD(subtotalCents)}</span></div>
                <div><span>Shipping</span><span>{fmtUSD(shippingCents)}</span></div>
                <div><span>Tax</span><span>{fmtUSD(taxCents)}</span></div>

                {signedIn && useWallet && walletAppliedCents > 0 && (
                  <div><span>Wallet applied</span><span>-{fmtUSD(walletAppliedCents)}</span></div>
                )}

                {remainderNetCents > 0 && (
                  <>
                    <div><span>Amount to bill (before fees)</span><span>{fmtUSD(remainderNetCents)}</span></div>
                    <div><span>Stripe processing fee</span><span>{fmtUSD(stripeFeeCents)}</span></div>
                  </>
                )}

                <div className="sf-total">
                  <span>Total due {totalDueCents === 0 ? '(wallet covered)' : ''}</span>
                  <span>{fmtUSD(totalDueCents)}</span>
                </div>
              </div>
            </>
          )}
        </aside>
      </div>

      <style>{`
:root{
  --sf-gold:#f5c65d;
  --sf-gold-10:rgba(245,198,93,.10);
  --sf-bd:rgba(255,255,255,.12);
  --sf-panel:#0b0b0b;
}
body { background:#000; color:#fff; font-family:'CustomFont',sans-serif; }
.sf-checkout{ max-width:1200px; margin:18px auto 80px; padding:0 16px; }
.sf-title{ font-size:2rem; font-weight:900; text-shadow:0 0 16px var(--sf-gold-10); }
.sf-grid{ display:grid; grid-template-columns:1.2fr .8fr; gap:18px; margin-top:14px; }
@media (max-width: 960px){ .sf-grid{ grid-template-columns:1fr; } }
.sf-panel{ background:var(--sf-panel); border:1px solid var(--sf-bd); border-radius:14px; padding:16px; }
.sf-h2{ font-size:1.2rem; margin:4px 0 12px; font-weight:900; }

.sf-label{ display:flex; flex-direction:column; gap:8px; margin-bottom:12px; font-weight:800; }
.sf-input{ background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.18); color:#fff; border-radius:12px; padding:12px 14px; outline:none; }
.sf-input[disabled]{ opacity:.7; cursor:not-allowed; }
.sf-input:focus{ border-color:var(--sf-gold); box-shadow:0 0 0 2px var(--sf-gold-10) inset; }
.sf-row{ display:flex; gap:12px; }
.sf-row .half{ flex:1; }

.sf-btn{ margin-top:6px; padding:.9rem 1.2rem; border-radius:12px; border:2px solid var(--sf-gold); background:var(--sf-gold); color:#000; font-weight:900; cursor:pointer; box-shadow:0 6px 18px rgba(245,198,93,.26); }
.sf-btn[disabled]{ opacity:.65; cursor:not-allowed; }
.sf-btn.ghost{ background:transparent; color:#f5c65d; border:1px solid rgba(255,255,255,.25); box-shadow:none; margin-left:8px; }

.sf-items{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
.sf-item{ display:flex; align-items:center; gap:10px; border:1px solid rgba(255,255,255,.1); border-radius:10px; padding:8px; }
.sf-item img{ width:46px; height:46px; object-fit:cover; border-radius:6px; border:1px solid rgba(255,255,255,.12); }
.sf-item-info{ flex:1; min-width:0; }
.sf-item-title{ font-weight:900; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sf-item-meta{ opacity:.8; font-size:.9rem; }
.sf-item-price{ color:#f5c65d; font-weight:900; }

.sf-totals{ margin-top:12px; display:flex; flex-direction:column; gap:6px; }
.sf-totals > div{ display:flex; justify-content:space-between; }
.sf-total{ font-size:1.15rem; font-weight:900; color:#f5c65d; margin-top:4px; }

.sf-empty{ opacity:.8; }

/* wallet toggle */
.sf-wallet-toggle{
  display:flex; align-items:center; gap:10px;
  background:rgba(255,255,255,.05);
  border:1px solid rgba(255,255,255,.12);
  border-radius:12px; padding:10px 12px; margin-bottom:12px;
}
.sf-wallet-title{ font-weight:900; }
.sf-wallet-sub{ opacity:.9; }
.sf-switch{ position:relative; width:44px; height:24px; display:inline-block; }
.sf-switch input{ opacity:0; width:0; height:0; }
.sf-slider{
  position:absolute; cursor:pointer; inset:0; background:#222; border-radius:999px; border:1px solid rgba(255,255,255,.18);
  transition:background .15s;
}
.sf-slider::before{
  content:''; position:absolute; height:18px; width:18px; left:3px; top:2px;
  background:#fff; border-radius:50%; transition:transform .15s;
}
.sf-switch input:checked + .sf-slider{ background:#f5c65d; }
.sf-switch input:checked + .sf-slider::before{ transform:translateX(20px); }
      `}</style>
    </div>
  );
}
